import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/simple-toaster';
import { Alert, AlertTitle, AlertDescription } from '@/app/components/ui/alert';
import { CreateAssetWizard } from '@/components/assets/create-asset-wizard';
import { useCreateAsset } from '@/hooks/use-assets';
import { useSubscription } from '@/hooks/use-billing';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import { handleApiError } from '@/lib/api-client';
import type { CreateAssetPayload } from '@/lib/types';

export function CreateAssetPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createMutation = useCreateAsset();
  const { data: subscriptionData } = useSubscription();

  const usage = subscriptionData?.subscription?.usage;
  const assetLimit = usage?.max_assets ?? null;
  const assetCount = usage?.asset_count ?? 0;
  const pctAssets = usage?.pct_assets ?? 0;
  const atCap = assetLimit !== null && assetCount >= assetLimit;
  const nearCap = assetLimit !== null && pctAssets >= 80;
  const creationBlocked = Boolean(
    subscriptionData?.subscription?.asset_limit?.is_creation_blocked
  );

  const initialSerialNumber = searchParams.get('serial') || undefined;

  const handleSubmit = async (data: CreateAssetPayload) => {
    try {
      const asset = await createMutation.mutateAsync(data);
      toast.success('Asset created successfully');
      navigate(`/assets/${asset.id}`);
    } catch (error) {
      const apiError = handleApiError(error);
      if (apiError.code === 'plan_limit_exceeded') {
        toast.error('Asset Creation Failed', {
          description: apiError.message,
          action: {
            label: 'Upgrade plan',
            onClick: () => {
              window.location.href = '/settings/subscription';
            },
          },
        });
        return;
      }
      toast.error(apiError.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageBreadcrumb items={[
        { label: 'Assets', href: '/assets' },
        { label: 'Create New Asset' },
      ]} />
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/assets">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create New Asset</h1>
          <p className="text-gray-600 mt-1">Add a new piece of equipment to inventory</p>
        </div>
      </div>

      <div className="max-w-3xl space-y-4">
        {creationBlocked && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Asset creation blocked</AlertTitle>
            <AlertDescription>
              Your asset-cap grace period has ended.{' '}
              <Link to="/settings/subscription" className="underline font-medium">
                Upgrade your plan
              </Link>{' '}
              or stay at or under the cap for seven consecutive days.
            </AlertDescription>
          </Alert>
        )}
        {!creationBlocked && atCap && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Asset cap grace period</AlertTitle>
            <AlertDescription>
              You can keep adding assets during the grace period.{' '}
              <Link to="/settings/subscription" className="underline font-medium">
                View your deadline and upgrade options
              </Link>
              .
            </AlertDescription>
          </Alert>
        )}
        {!creationBlocked && !atCap && nearCap && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Approaching asset limit</AlertTitle>
            <AlertDescription>
              You are using {pctAssets.toFixed(1)}% of your {assetLimit} asset limit.{' '}
              <Link to="/settings/subscription" className="underline font-medium">
                Upgrade your plan
              </Link>{' '}
              to avoid running out.
            </AlertDescription>
          </Alert>
        )}
        {!creationBlocked && (
          <CreateAssetWizard
            onSubmit={handleSubmit}
            isSubmitting={createMutation.isPending}
            onCancel={() => navigate('/assets')}
            initialSerialNumber={initialSerialNumber}
          />
        )}
      </div>
    </div>
  );
}
